/** Acompanhar alguém numa sessão partilhada: o que se manda e o que se
 *  aplica da vista (câmara e selecção) de cada janela.
 *
 *  Pedido do Rui (18/10/2026): a Sala de revisão à parte, em que o outro
 *  tinha de abrir o modelo à mão, "não faz muito sentido. Devia fazer
 *  tudo parte de partilhar a sessão. Dentro do partilhar a sessão, podes
 *  ou não escolher acompanhar o anfitrião." Numa sessão partilhada toda
 *  a gente já tem os mesmos modelos abertos; a vista viaja pelo canal da
 *  sessão (backend/modules/bcf/ao_vivo.py, `vista_para_difundir`), que
 *  já exige acesso à partilha. Os modelos identificam-se pelo sha256 do
 *  ficheiro, não pelo nome, que pode ser outro em cada browser. */

import type { EstadoCamara } from '@viewer/room/roomProtocol'

export interface SeleccaoPorModelo {
  sha256: string
  ids: number[]
}

export interface VistaRecebida {
  de: number
  utilizador: string
  nome: string
  camara: EstadoCamara | null
  /** null: a mensagem não falava da selecção; []: sem selecção. */
  seleccao: SeleccaoPorModelo[] | null
}

/** O mesmo limite que o servidor aceita por modelo. */
export const MAXIMO_DE_IDS_POR_MODELO = 500

/** De quanto em quanto tempo se lê a câmara para a mandar (só se mudou). */
export const LEITURA_DA_CAMARA_MS = 100

/** Uma vista completa de vez em quando, para quem começa a acompanhar
 *  não ficar à espera do próximo movimento. */
export const VISTA_COMPLETA_MS = 2000

interface Alvo {
  modelId: string
  localId: number
}

interface ModeloAberto {
  id: string
  sha256?: string | null
}

/** A selecção desta janela, por modelo (sha256), para mandar. */
export function seleccaoParaEnviar(
  alvos: readonly Alvo[],
  modelos: readonly ModeloAberto[],
): SeleccaoPorModelo[] {
  const shaDe = new Map(modelos.filter((m) => m.sha256).map((m) => [m.id, m.sha256 as string]))
  const porSha = new Map<string, number[]>()
  for (const a of alvos) {
    const sha = shaDe.get(a.modelId)
    if (!sha) continue
    const lista = porSha.get(sha) ?? []
    if (lista.length < MAXIMO_DE_IDS_POR_MODELO) lista.push(a.localId)
    porSha.set(sha, lista)
  }
  return [...porSha.entries()].map(([sha256, ids]) => ({ sha256, ids }))
}

/** Os alvos desta janela que correspondem à selecção recebida (só nos
 *  modelos que também estão abertos aqui). */
export function alvosDaSeleccao(
  seleccao: readonly SeleccaoPorModelo[],
  modelos: readonly ModeloAberto[],
): Alvo[] {
  const idDe = new Map(
    modelos.filter((m) => m.sha256).map((m) => [(m.sha256 as string).toLowerCase(), m.id]),
  )
  const out: Alvo[] = []
  for (const s of seleccao) {
    const modelId = idDe.get(s.sha256.toLowerCase())
    if (!modelId) continue
    for (const localId of s.ids) out.push({ modelId, localId })
  }
  return out
}

/** Chave de uma selecção, para não aplicar nem mandar a mesma duas vezes. */
export function chaveDaSeleccao(seleccao: readonly SeleccaoPorModelo[]): string {
  return seleccao
    .map((s) => `${s.sha256.toLowerCase()}:${[...s.ids].sort((a, b) => a - b).join(',')}`)
    .sort()
    .join('|')
}

/** A mensagem que esta janela manda pelo canal da sessão. */
export function mensagemDaVista(
  camara: EstadoCamara | null,
  seleccao: SeleccaoPorModelo[] | null,
): Record<string, unknown> | null {
  if (!camara && !seleccao) return null
  const m: Record<string, unknown> = { tipo: 'vista' }
  if (camara) m.camara = { posicao: camara.posicao, alvo: camara.alvo }
  if (seleccao) m.seleccao = seleccao
  return m
}

const eNumero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

function ponto(v: unknown): [number, number, number] | null {
  return Array.isArray(v) && v.length === 3 && v.every(eNumero)
    ? [v[0] as number, v[1] as number, v[2] as number]
    : null
}

/** O que chega pelo socket é dado: valida-se antes de lhe tocar. */
export function vistaRecebida(v: Record<string, unknown>): VistaRecebida | null {
  if (typeof v.de !== 'number' || typeof v.utilizador !== 'string' || typeof v.nome !== 'string') {
    return null
  }
  let camara: EstadoCamara | null = null
  if (v.camara !== undefined && v.camara !== null) {
    const c = v.camara as Record<string, unknown>
    const posicao = ponto(c?.posicao)
    const alvo = ponto(c?.alvo)
    if (!posicao || !alvo) return null
    camara = { posicao, alvo }
  }
  let seleccao: SeleccaoPorModelo[] | null = null
  if (Array.isArray(v.seleccao)) {
    seleccao = []
    for (const s of v.seleccao as unknown[]) {
      const r = s as Record<string, unknown> | null
      if (!r || typeof r.sha256 !== 'string' || !Array.isArray(r.ids)) return null
      if (!r.ids.every((i) => Number.isInteger(i))) return null
      seleccao.push({ sha256: r.sha256, ids: r.ids as number[] })
    }
  }
  if (!camara && !seleccao) return null
  return { de: v.de, utilizador: v.utilizador, nome: v.nome, camara, seleccao }
}
